import type { RootState } from '#app/store'
import { isComponentConfigurationAction } from '#store/componentConfiguration'
import { isLayoutConfigAction } from '#store/layoutConfiguration'
import { buildLocalStorageMiddleware, type ReduxVersion } from '#store/middleware/localStorage'
import {
  isConfigurationAction,
  type RecoverableConfiguration,
  setApiConfiguration,
  type TruckConfiguration,
} from '#store/slices/configurationSlice.ts'
import { isLocalConfigAction } from '#store/slices/localConfigSlice'
import { isUserDataAction, replaceUserData } from '#store/slices/userDataSlice'

const storageTypeName = 'maic-frontend-local-storage' as const

const convertConfigurationToPersistence = (configuration: TruckConfiguration): RecoverableConfiguration => {
  const { step, type: _type, productGroups: _productGroups, checks: _checks, ...rest } = configuration
  return { ...rest, type: 'recoverable-configuration', step: { view: step.view } }
}

const withTabRecovery = VITE_ENABLE_RECOVERY

export const makeAppLocalStorage = () =>
  buildLocalStorageMiddleware<RootState, typeof storageTypeName>({
    db: 'maic-frontend',
    key: 'root-store',
    version: 'v0.2.0' as ReduxVersion,
    type: storageTypeName,
    makeStoredState: (clonedState) => {
      const copy: Partial<RootState> = {}

      const recoverableConfigs = withTabRecovery
        ? Object.values(clonedState.truckConfigurations.configurations)
            .filter((x) => !!x)
            .map(convertConfigurationToPersistence)
        : []

      copy.localConfig = clonedState.localConfig
      copy.layoutConfiguration = clonedState.layoutConfiguration
      copy.componentConfiguration = clonedState.componentConfiguration
      copy.truckConfigurations = {
        meta: { ...clonedState.truckConfigurations.meta },
        configurations: {},
        leadingConfiguration: clonedState.truckConfigurations.leadingConfiguration,
        closedConfigurations: [...clonedState.truckConfigurations.closedConfigurations],
        recoverableConfigurations: recoverableConfigs,
        apiConfiguration: { ...clonedState.truckConfigurations.apiConfiguration },
        marketData: {},
      }
      copy.userData = clonedState.userData

      return copy
    },
    shouldSave: (action) => {
      return (
        isLayoutConfigAction(action) ||
        isComponentConfigurationAction(action) ||
        isLocalConfigAction(action) ||
        isUserDataAction(action) ||
        setApiConfiguration.match(action) ||
        replaceUserData.match(action) ||
        (withTabRecovery && isConfigurationAction(action))
      )
    },
  })
